import React, { useState, useEffect, useRef } from "react";
import {
  useBlockTravellerMutation,
  useGetUserByIdQuery,
  useLazyGetTravellerProfileTableQuery,
  useLazyGetUserByIdQuery,
  usePrimaryBadgesQuery,
  useSpecialBadgesQuery,
  useAssignPointsMutation,
  useAssignPrimaryBadgeMutation,
  useAssignSpecialBadgeMutation
} from "../../../../services/Api";
import LoaderSpiner from "../../../../components/loaderSpiner";
import { Pagination } from "../../../../components/Pagination";
import { RiSearch2Line } from "react-icons/ri";

import Dropdown from "../../../../components/Dropdown";
import { toast } from "react-toastify";
import dateFormat, { masks } from "dateformat";
import { BASE_URL_IMAGE } from "../../../../services/ApiEndpoints";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AssignModal from "./AssignModel";

const ProfilesTable = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetails, setModalUserDetails] = useState(null);
  const [fetchUser, { data: userDetailsData, isLoading: userLoading }] = useLazyGetUserByIdQuery();
  const dropdownRef = useRef(null);
  const [trigger, { data, isLoading, error }] =
    useLazyGetTravellerProfileTableQuery();
  const { data: primaryBadgesData } = usePrimaryBadgesQuery();
  const { data: specialBadgesData } = useSpecialBadgesQuery();
  const [assignPrimaryBadge, { isLoading: isAssigningPrimary }] = useAssignPrimaryBadgeMutation();
  const [assignSpecialBadge, { isLoading: isAssigningSpecial }] = useAssignSpecialBadgeMutation();
  const [assignPoints, { isLoading: isAssigningPoints }] = useAssignPointsMutation();

  const [assignModal, setAssignModal] = useState({
    open: false,
    type: null, // 'primary', 'special', 'points'
    userId: null,
  });
  const [dropdownActive, setDropdownActive] = useState(null);

  const [blockTraveller, { isError, isSuccess }] = useBlockTravellerMutation();

  const handleBlockTraveller = async (id, duration) => {
    try {
      const data = duration === "unblock" ? {} : { blockDuration: duration };
      const response = await blockTraveller({ id, data });

      if (response?.error) {
        toast.error("Failed to update user status");
      } else {
        toast.success(
          response?.data?.message || "Action completed successfully"
        );
        await trigger();
      }
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  useEffect(() => {
    trigger({ page: currentPage });
  }, [currentPage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownActive(null);
      }
    };
    if (dropdownActive !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownActive]);

  if (isLoading) {
    return (
      <p>
        <LoaderSpiner />
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-errorcolor">
        Error loading data. Please try again later.
      </p>
    );
  }

  const TravellerTableData = Array.isArray(data?.users)
    ? data?.users
    : [];
  const totalPages = data?.totalPages;

  const validatePhoneNumber = (phone) => {
    const regex = /^(\+?\d{1,3}?)?(\d{10})$/;
    return regex.test(phone);
  };

  const toggleDropdown = (index) => {
    setDropdownActive((prev) => (prev === index ? null : index));
  };

  const filteredProfiles = TravellerTableData.filter((row) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      row?._id?.toLowerCase()?.includes(searchLower) ||
      row?.firstName?.toLowerCase()?.includes(searchLower) ||
      row?.lastName?.toLowerCase()?.includes(searchLower) ||
      row?.email?.toLowerCase()?.includes(searchLower) ||
      (typeof row?.phoneNumber === "string" &&
        row?.phoneNumber?.toLowerCase()?.includes(searchLower)) ||
      row?.role?.toLowerCase()?.includes(searchLower)
    );
  });

  const handleViewUser = async (userId) => {
    setSelectedUserId(userId);
    setViewModalOpen(true);
    setModalUserDetails(undefined); // Clear previous data
    const result = await fetchUser(userId);
    if (result?.data) {
      setModalUserDetails(result?.data);
    } else {
      setModalUserDetails(null); // Explicitly set to null if not found
    }
  };
  console.log("filteredProfiles", userDetails)



  const handleAssign = async (type, badgeId, points) => {
    try {
      let res;
      if (type === "primary") {
        res = await assignPrimaryBadge({ id: assignModal.userId, data: { badgeId: badgeId } }).unwrap();
      } else if (type === "special") {
        res = await assignSpecialBadge({ id: assignModal.userId, data: { badgeId: badgeId } }).unwrap();
      } else if (type === "points") {
        res = await assignPoints({ id: assignModal.userId, data: { points: Number(points) } }).unwrap();
      }
      setAssignModal({ open: false, type: null, userId: null });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: res?.message || "Assigned successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.data?.message || "Failed to assign.",
      });
    }
  };

  const getDropdownOptions = (row) => {
    const baseOptions = [
      {
        label: "View",
        onClick: (e) => {
          e.stopPropagation();
          handleViewUser(row?._id);
        },
      },
      {
        label: "Add Primary Badge",
        onClick: (e) => {
          e.stopPropagation();
          setAssignModal({ open: true, type: "primary", userId: row?._id });
        },
      },
      {
        label: "Add Secondry Badge",
        onClick: (e) => {
          e.stopPropagation();
          setAssignModal({ open: true, type: "special", userId: row?._id });
        },
      },
      {
        label: "Add Points",
        onClick: (e) => {
          e.stopPropagation();
          setAssignModal({ open: true, type: "points", userId: row?._id });
        },
      },
      {
        label: "Delete",
        onClick: (e) => {
          e.stopPropagation();
        },
      },
    ];

    // If user is blocked (checking !isActive), only show unblock option
    if (!row?.isActive) {
      return [
        {
          label: "Unblock",
          onClick: async (e) => {
            e.stopPropagation();
            await handleBlockTraveller(row?._id, "unblock");
          },
        },
        ...baseOptions,
      ];
    }

    // If user is active, show block options
    return [
      {
        label: "Block for 15 Days",
        onClick: (e) => {
          e.stopPropagation();
          handleBlockTraveller(row?._id, "15days");
        },
      },
      {
        label: "Block for 6 Months",
        onClick: (e) => {
          e.stopPropagation();
          handleBlockTraveller(row?._id, "6months");
        },
      },
      {
        label: "Block Permanently",
        onClick: (e) => {
          e.stopPropagation();
          handleBlockTraveller(row?._id, "indefinite");
        },
      },
      ...baseOptions,
    ];
  };

  const badgeImagePrimary = Array.isArray(primaryBadgesData?.data) && primaryBadgesData?.data?.length > 0 && userDetails?.data?.badgePoints
    ? [...primaryBadgesData?.data]
      .sort((a, b) => a.points - b.points)
      .reverse()
      .find(badge => userDetails?.data.badgePoints >= badge.points)?.badgeImage
    : null;

  return (
    <div className="bg-white p-6 rounded-[1.75rem] shadow-md mb-4 md:mb-0 overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-['Roboto_Mono'] text-2xl font-bold">
          Traveller Profiles
        </h2>
        <div className="relative w-1/4 md:w-[296px]">
          <RiSearch2Line className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full p-2 pl-10 bg-[#F9F9F9] shadow-lg rounded-[1.75rem] outline-none py-2"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-auto border-separate border-spacing-y-4 min-w-0">
          <thead>
            <tr className="bg-tablecolor">
              <th className="p-3 text-left w-8 rounded-l-md">
                <input type="checkbox" className="rounded border-normalGray" />
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                ID
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                First Name
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Last Name
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Nationality
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Email
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Phone No
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Role
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Gender
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Tags
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium">
                Total Packing List Saved
              </th>
              <th className="p-3 text-left text-black font-roboto-mono text-xs font-medium rounded-r-md">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredProfiles.length > 0 ? (
              filteredProfiles.map((row, index) => (
                <tr
                  key={row?.id}
                  className="bg-white hover:bg-tablecolor rounded-md"
                >
                  <td className="p-3 rounded-l-md border-t border-b border-l border-solid border-bordercolor">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?._id}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.firstName}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.lastName}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.nationality}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.email}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {validatePhoneNumber(row?.phoneNumber)
                      ? row?.phoneNumber
                      : "Invalid Phone Number"}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.role}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.gender}
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    <span
                      className={`p-2 rounded-md ${row?.isActive
                        ? "bg-bgprimary text-dark"
                        : "bg-errorcolor text-white whitespace-nowrap"
                        }`}
                    >
                      {row?.isActive
                        ? "Active"
                        : `Blocked until ${dateFormat(
                          row?.blockedUntil,
                          "ddd, MMM dS"
                        )}`}
                    </span>
                  </td>
                  <td className="p-3 text-normalGray font-quicksand text-sm font-semibold border-t border-b border-solid border-bordercolor">
                    {row?.savedPackingListCount || "N/A"}
                  </td>
                  <td className="p-3 rounded-r-md border-t border-b border-r border-solid border-bordercolor">
                    <div ref={dropdownActive === index ? dropdownRef : null}>
                      <Dropdown
                        active={dropdownActive === index}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(index);
                        }}
                        options={getDropdownOptions(row)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="p-3 text-center text-normalGray">
                  No profiles available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-2">
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>


      {viewModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
            <button
              className="absolute top-2 right-2 text-xl"
              onClick={() => setViewModalOpen(false)}
            >
              &times;
            </button>
            {userLoading ? (
              <div className="animate-pulse space-y-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-aths rounded-full" />
                  <div>
                    <div className="h-4 bg-aths rounded w-32 mb-2" />
                    <div className="h-3 bg-aths rounded w-20" />
                  </div>
                </div>
                <div className="h-4 bg-aths rounded w-24 mb-2" />
                <div className="h-12 bg-aths rounded w-12 mb-2" />
                <div className="h-4 bg-aths rounded w-28 mb-2" />
                <div className="flex gap-2">
                  <div className="w-16 h-16 bg-aths rounded" />
                  <div className="w-16 h-16 bg-aths rounded" />
                  <div className="w-16 h-16 bg-aths rounded" />
                </div>
                <div className="h-4 bg-aths rounded w-20 mt-4" />
                <div className="h-4 bg-aths rounded w-32" />
                <div className="h-4 bg-aths rounded w-32" />
                <div className="h-4 bg-aths rounded w-32" />
              </div>
            ) : userDetails ? (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={
                      userDetails?.data?.profilePicture
                        ? `${BASE_URL_IMAGE}${userDetails?.data?.profilePicture}`
                        : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          (userDetails?.data?.firstName || "") +
                          " " +
                          (userDetails?.data?.lastName || "")
                        )}&background=cccccc&color=222222&size=128`
                    }
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://ui-avatars.com/api/?name=User&background=cccccc&color=222222&size=128";
                    }}
                  />
                  <div>
                    <h3 className="text-xl font-bold font-quicksand text-dark">
                      {userDetails?.data?.firstName} {userDetails?.data?.lastName}
                    </h3>
                    <p className="text-gray-600 font-quicksand font-semibold">
                      {userDetails?.data?.nationality}
                    </p>
                  </div>
                </div>
                <p className="font-quicksand text-normalGray font-bold">
                  <span className="text-dark">Primary Badges</span>
                </p>
                {/* Calculate badgeImagePrimary only when userDetails is available */}
                {(() => {
                  const badgeImagePrimary =
                    Array.isArray(primaryBadgesData?.data) &&
                      primaryBadgesData?.data?.length > 0 &&
                      userDetails?.data?.badgePoints
                      ? [...primaryBadgesData?.data]
                        .sort((a, b) => a.points - b.points)
                        .reverse()
                        .find(
                          (badge) => userDetails?.data.badgePoints >= badge.points
                        )?.badgeImage
                      : null;
                  return badgeImagePrimary ? (
                    <img
                      src={`${BASE_URL_IMAGE}${badgeImagePrimary}`}
                      alt="Primary"
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <p className="font-quicksand text-normalGray font-semibold">
                      No primary badges
                    </p>
                  );
                })()}


                <p className="font-quicksand text-normalGray font-bold mt-4">
                  <span className="text-dark">Special Badges</span>
                </p>
                {(!userDetails?.data?.badges?.special ||
                  userDetails?.data?.badges?.special.length === 0) && (
                    <p className="font-quicksand text-normalGray font-semibold">
                      No special badges
                    </p>
                  )}
                <div className="flex flex-nowrap gap-3 mt-2">
                  {userDetails?.data?.badges?.special?.map((badge, idx) => (
                    <div
                      key={idx}
                      className="relative w-16 h-16 bg-[#edeccf] rounded flex items-center justify-center border border-[#d9dab2]"
                    >
                      {badge.badgeImage ? (
                        <img
                          src={`${BASE_URL_IMAGE}${badge.badgeImage}`}
                          alt={badge.name}
                          className="w-12 h-12 object-contain"
                        />
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="font-quicksand text-normalGray font-semibold mt-4">
                  <span className="text-dark">Points:</span>{" "}
                  {userDetails?.data?.badgePoints || "0"}
                </p>
                <p className="font-quicksand text-normalGray font-semibold">
                  <span className="text-dark">Backer Number:</span>{" "}
                  {userDetails?.data?.backerNumber}
                </p>
                <p className="font-quicksand text-normalGray font-semibold">
                  <span className="text-dark">Email:</span>{" "}
                  {userDetails?.data?.email}
                </p>
                <p className="font-quicksand text-normalGray font-semibold">
                  <span className="text-dark">Phone:</span>{" "}
                  {userDetails?.data?.phoneNumber}
                </p>
                <p className="font-quicksand text-normalGray font-semibold">
                  <span className="text-dark">Role:</span>{" "}
                  {userDetails?.data?.role}
                </p>
                <p className="font-quicksand text-normalGray font-semibold break-all">
                  <span className="text-dark">Description:</span>{" "}
                  {userDetails?.data?.description || "N/A"}
                </p>
              </div>

            ) : (
              <div className="animate-pulse space-y-4">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-aths rounded-full" />
                  <div>
                    <div className="h-4 bg-aths rounded w-32 mb-2" />
                    <div className="h-3 bg-aths rounded w-20" />
                  </div>
                </div>
                <div className="h-4 bg-aths rounded w-24 mb-2" />
                <div className="h-12 bg-aths rounded w-12 mb-2" />
                <div className="h-4 bg-aths rounded w-28 mb-2" />
                <div className="flex gap-2">
                  <div className="w-16 h-16 bg-aths rounded" />
                  <div className="w-16 h-16 bg-aths rounded" />
                  <div className="w-16 h-16 bg-aths rounded" />
                </div>
                <div className="h-4 bg-aths rounded w-20 mt-4" />
                <div className="h-4 bg-aths rounded w-32" />
                <div className="h-4 bg-aths rounded w-32" />
                <div className="h-4 bg-aths rounded w-32" />
              </div>
            )}
          </div>
        </div>
      )}
      <AssignModal
        open={assignModal.open}
        onClose={() => setAssignModal({ open: false, type: null, userId: null })}
        type={assignModal.type}
        userId={assignModal.userId}
        primaryBadges={primaryBadgesData?.data || []}
        specialBadges={specialBadgesData?.data || []}
        onSubmit={handleAssign}
        isLoading={
          assignModal.type === "primary"
            ? isAssigningPrimary
            : assignModal.type === "special"
              ? isAssigningSpecial
              : isAssigningPoints
        }
      />
    </div>
  );
};

export default ProfilesTable;
